<template>
  <div>
    <HttpContentView :endpoint="utilities.path('communities/@/users/invites', communityId)" v-model:reload="reloadPending">
      <template v-slot:content="{ data }">
        <div v-if="data.length > 0" class="mb-4">
          <h2>{{ translate("community.invites.pending") }}</h2>
          <div class="card">
            <div class="table-responsive">
              <div class="table">
                <div class="table-header">
                  <div class="table-header-cell">{{ translate("email") }}</div>
                  <div class="table-header-cell">{{ translate("actions") }}</div>
                </div>
                <div class="table-body">
                  <div class="table-row" v-for="item in data" :key="item.id">
                    <div class="table-column">
                      {{ item.email }}
                    </div>
                    <div class="table-column">
                      <div class="d-flex align-items-center g-2">
                        <TargetedModalHttpFormView v-if="authorization.communityAdministrator()" size="small" :endpoint="utilities.path('communities/users/invites/@', item.id)" method="delete" @success="reloadPending = true">
                          <template v-slot:target="{ show }">
                            <ButtonView type="soft" color="danger" size="small" @click="show">
                              <i class="fas fa-trash"></i>
                            </ButtonView>
                          </template>
                          <template v-slot:form="{ submit, loading, hide }">
                            <SpinnerView :active="loading" color="danger"/>
                            <div class="d-flex align-items-center g-4">
                              <ButtonView type="normal" color="danger" class="w-100" @click="submit">
                                {{ translate("delete") }}
                              </ButtonView>
                              <ButtonView type="normal" color="secondary" class="w-100" @click="hide">
                                {{ translate("cancel") }}
                              </ButtonView>
                            </div>
                          </template>
                        </TargetedModalHttpFormView>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </HttpContentView>
    <CollectionView :endpoint="utilities.path('communities/@/users', communityId)" v-model:reload="reload">
      <template v-slot:header>
        <div class="d-flex align-items-center justify-content-between w-100">
          <h4 class="mb-0">{{ translate("users") }}</h4>
          <ButtonView type="normal" color="primary" @click="creating = true">
            <div class="d-flex align-items-center g-2">
              <i class="fas fa-user-plus"></i>
              <span>{{ translate("invite") }}</span>
            </div>
          </ButtonView>
        </div>
      </template>
      <template v-slot:content="{ items }">
        <div class="card">
          <div class="table-responsive">
            <div class="table">
              <div class="table-header">
                <div class="table-header-cell">{{ translate("name") }}</div>
                <div class="table-header-cell">{{ translate("email") }}</div>
                <div class="table-header-cell">{{ translate("roles") }}</div>
                <div class="table-header-cell">{{ translate("actions") }}</div>
              </div>
              <div class="table-body">
                <div class="table-row" v-for="item in items" :key="item.id">
                  <div class="table-column">
                    <UserView :user="item.user"/>
                  </div>
                  <div class="table-column">
                    {{ item.user.email }}
                  </div>
                  <div class="table-column">
                    <div class="d-flex align-items-center g-2">
                      <RoleView v-for="role in item.roles" :key="role.id" :role="role"/>
                    </div>
                  </div>
                  <div class="table-column">
                    <div class="d-flex align-items-center g-2">
                      <ButtonView v-if="authorization.permission('roles.assign')" type="soft" color="info" size="small" @click="editingRoles = item">
                        <i class="fas fa-list-ul"></i>
                      </ButtonView>
                      <TargetedModalHttpFormView v-if="info.community && item.user.id !== info.community.owner.id" size="small" :endpoint="utilities.path('communities/@/users/@', communityId, item.user.id)" method="delete" @success="reload = true">
                        <template v-slot:target="{ show }">
                          <ButtonView type="soft" color="danger" size="small" @click="show">
                            <i class="fas fa-trash"></i>
                          </ButtonView>
                        </template>
                        <template v-slot:form="{ submit, loading, hide }">
                          <SpinnerView :active="loading" color="danger"/>
                          <div class="d-flex align-items-center g-4">
                            <ButtonView type="normal" color="danger" class="w-100" @click="submit">
                              {{ translate("delete") }}
                            </ButtonView>
                            <ButtonView type="normal" color="secondary" class="w-100" @click="hide">
                              {{ translate("cancel") }}
                            </ButtonView>
                          </div>
                        </template>
                      </TargetedModalHttpFormView>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </CollectionView>
    <ModalView v-model:active="creating">
      <template v-slot:body>
        <HttpFormView :endpoint="utilities.path('communities/@/users/invites', communityId)" method="post" @success="creating = false; toasts.create({ color: 'success', content: translate('operation_successful') }); reloadPending = true">
          <template v-slot:form="{ content, errors, submit, loading }">
            <InputView type="text" v-model:value="content.email" :title="translate('email')" :errors="errors.email" required/>
            <div class="d-flex align-items-center g-4 mt-4">
              <ButtonView type="normal" color="primary" class="w-100" @click="submit">
                {{ translate("invite.send") }}
              </ButtonView>
              <ButtonView type="normal" color="secondary" class="w-100" @click="creating = false">
                {{ translate("cancel") }}
              </ButtonView>
            </div>
            <SpinnerView color="primary" :active="loading"/>
          </template>
        </HttpFormView>
      </template>
    </ModalView>
    <ModalView :active="editingRoles !== undefined" @update:active="editingRoles = undefined">
      <template v-slot:header>
        <div class="d-flex align-items-center justify-content-between">
          <h3 class="mb-0">{{ translate("roles") }}</h3>
          <ModalCloseButtonView @click="editingRoles = undefined"/>
        </div>
      </template>
      <template v-slot:body>
        <HttpFormView :endpoint="utilities.path('communities/@/users/@/roles', communityId, editingRoles.user.id)" method="post" :content="{ roles: editingRoles.roles?.map(role => role.id) }" @success="editingRoles = undefined; reload = true" >
          <template v-slot:form="{ content, errors, submit }">
            <HttpSelectInputView :endpoint="utilities.path('communities/@/roles/search', communityId)" :title="translate('roles')" :errors="errors.roles" required multiple v-model:value="content.roles">
              <template v-slot:option="{ option, preview }">
                <div v-if="preview" class="d-inline-block">
                  <RoleView :role="option"/>
                </div>
                <span v-else>{{ option.name }}</span>
              </template>
            </HttpSelectInputView>
            <div class="d-flex align-items-center g-4 mt-4">
              <ButtonView type="normal" color="primary" class="w-100" @click="submit">
                {{ translate("save") }}
              </ButtonView>
              <ButtonView type="normal" color="secondary" class="w-100" @click="editingRoles = undefined">
                {{ translate("cancel") }}
              </ButtonView>
            </div>
          </template>
        </HttpFormView>
      </template>
    </ModalView>
  </div>
</template>

<script setup>
import {
  ButtonView,
  CollectionView,
  utilities,
  ModalView,
  HttpFormView,
  HttpSelectInputView,
  TargetedModalHttpFormView,
  ModalCloseButtonView,
  SpinnerView,
  toasts,
  InputView,
  HttpContentView
} from "@niftybit/vue";
import {ref} from "vue";
import UserView from "@/components/other/UserView.vue";
import RoleView from "@/systems/modules/roles/RoleView.vue";
import authorization from "@/authorization";
import info from "@/info";

defineProps({
  communityId: String
});

const reload = ref(false);
const reloadPending = ref(false);
const creating = ref(false);
const editingRoles = ref(undefined);

</script>